import React from 'react';
import _merge from 'lodash/merge';

import { getContext } from 'common';
import Tabs, { Tab } from 'components/Tabs';
import Styled from './styles';


interface HorizontalNavigationContextInterface {
  oneLine?: boolean,
  theme?: any
}

const HorizontalNavigationContext = React.createContext<HorizontalNavigationContextInterface | null>({});

export interface HorizontalNavigationProps extends React.ComponentPropsWithRef<'div'> {
  /** Align to the left or center (use center for smaller container). */
  alignment?: 'left' | 'center';
  /** If true, the label text will try to stay on 1 line. */
  oneLine?: boolean;
  /** Set to true, each tab will take as much width as possible within the tab container. This prop has no affect on icon tabs. */
  fullWidth?: boolean;
  /** Enables scrollable tabs (enabled by default) */
  scroll?: boolean;
  /** Right-to-left. It's preferred that RTL is toggled at an application level. However if you need a component level override, this prop can help. */
  rtl?: boolean;
  /** Render prop to render custom selected-indicator. { selectedRef, innerRef, containerRef } */
  renderSelectedIndicator?: (arg:{
    selectedRef?: any,
    innerRef?: any,
    containerRef?: any
  }) => JSX.Element;
  /** Accepts multiple <Tab /> children. */
  children: React.ReactNode;
  /** To override the theme please refer to the "Readme" tab to learn more. */
  theme?: any;
}

/** HorizontalNavigation - An AppBanner variant of <Tabs /> component (also called a Perspective Bar).
 * Any other props will be passed to the Tabs component.
 */
const HorizontalNavigation:React.FC = React.forwardRef<HTMLElement, HorizontalNavigationProps>((props: HorizontalNavigationProps, ref) =>
  getContext(({ RTL, theme }: { theme?: any, RTL: boolean }) => {
    const {
      alignment,
      children,
      scroll,
      oneLine,
      rtl,
      theme: userTheme,
      ...otherProps
    } = props;
    const resolvedTheme = _merge({}, theme.HORIZONTAL_NAVIGATION_TAB, userTheme.HORIZONTAL_NAVIGATION_TAB);
    const navThemeCloseButton = theme.HORIZONTAL_NAVIGATION.closeButton;
    return (
      <Styled.HorizontalNavigation
        $content={navThemeCloseButton.content}
        $hovercontent={navThemeCloseButton.hovered.content}
        $hoverfill={navThemeCloseButton.hover}
        theme={resolvedTheme.navigation}
        rtl={RTL || rtl}
      >
        <Tabs
          alignment={alignment}
          scroll={scroll}
          {...otherProps}
          theme={resolvedTheme}
          variant="horizontal-navigation"
          ref={ref}
          rtl={rtl}
        >
          <HorizontalNavigationContext.Provider
            value={{ oneLine, theme: resolvedTheme }}
          >
            {children}
          </HorizontalNavigationContext.Provider>
        </Tabs>
      </Styled.HorizontalNavigation>
    );
  }));

HorizontalNavigation.displayName = 'HorizontalNavigation';

HorizontalNavigation.defaultProps = {
  ...Tabs.defaultProps,
  oneLine: true,
  alignment: 'left',
  theme: { HORIZONTAL_NAVIGATION_TAB: {} },
};

export { HorizontalNavigation as default, HorizontalNavigationContext, Tab };
